import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Calendar,
  Users,
  Clock,
  TrendingUp,
  Play,
  Pause,
  Edit,
  Copy,
  Trash2,
  Eye
} from 'lucide-react'
import { useProgramStore } from '@/stores/programStore'
import { ProgramService } from '@/services'
import { apiService } from '@/services/api'
import { Program } from '@/types'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { format } from 'date-fns'

interface ProgramListViewProps {
  onCreateProgram: () => void
}

export function ProgramListView({ onCreateProgram }: ProgramListViewProps) {
  const navigate = useNavigate()
  const {
    filteredPrograms,
    programsLoading,
    programFilters,
    setProgramFilters,
    setPrograms,
    setProgramsLoading,
    updateProgram,
    removeProgram,
    duplicateProgram
  } = useProgramStore()

  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'status' | 'client'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    loadPrograms()
  }, [])

  const loadPrograms = async () => {
    setProgramsLoading(true)
    try {
      const response = await ProgramService.getPrograms(1, 50)
      setPrograms(response.data)
    } catch (error) {
      console.error('Failed to load programs:', error)
    } finally {
      setProgramsLoading(false)
    }
  }

  const handleSearch = (query: string) => {
    setProgramFilters({ search: query })
  }

  const handleStatusFilter = (status: Program['status'] | 'all') => {
    setProgramFilters({ status: status === 'all' ? undefined : status })
  }

  const handleProgramAction = async (programId: string, action: string) => {
    switch (action) {
      case 'view':
        navigate(`/programs/${programId}`)
        break
      case 'edit':
        navigate(`/programs/${programId}/edit`)
        break
      case 'duplicate':
        duplicateProgram(programId)
        break
      case 'activate':
        await updateProgram(programId, { status: 'active' })
        break
      case 'pause':
        await updateProgram(programId, { status: 'paused' })
        break
      case 'delete':
        if (window.confirm('Are you sure you want to delete this program?')) {
          try {
            await apiService.deleteProgram(programId)
            removeProgram(programId)
          } catch (error) {
            console.error('Failed to delete program:', error)
          }
        }
        break
    }
  }

  const getStatusBadge = (status: Program['status']) => {
    const statusStyles = {
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
      paused: 'bg-yellow-100 text-yellow-700'
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const sortedPrograms = [...filteredPrograms].sort((a, b) => {
    let aValue: any, bValue: any
    
    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase()
        bValue = b.name.toLowerCase()
        break
      case 'createdAt':
        aValue = new Date(a.createdAt)
        bValue = new Date(b.createdAt)
        break
      case 'status':
        aValue = a.status
        bValue = b.status
        break
      case 'client':
        aValue = a.clientId // In a real app, you'd join with client names
        bValue = b.clientId
        break
      default:
        return 0
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
    }
  })

  if (programsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Programs</h1>
          <p className="text-gray-600">
            Create and manage workout programs for your clients
          </p>
        </div>
        
        <button 
          onClick={onCreateProgram}
          className="coach-button-primary px-4 py-2"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Program
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search programs..."
            value={programFilters.search || ''}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Filter className="w-4 h-4" />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={programFilters.status || 'all'}
                onChange={(e) => handleStatusFilter(e.target.value as Program['status'] | 'all')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
                <option value="status">Status</option>
                <option value="client">Client</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order
              </label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Programs Grid/Table */}
      {sortedPrograms.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Calendar className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No programs found</h3>
          <p className="text-gray-600 mb-4">
            {programFilters.search || programFilters.status 
              ? 'Try adjusting your search or filters'
              : 'Create your first program to get started'
            }
          </p>
          <button 
            onClick={onCreateProgram}
            className="coach-button-primary px-4 py-2"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Program
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Program
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPrograms.map((program) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{program.name}</div>
                        {program.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {program.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                          <Users className="w-4 h-4 text-gray-600" />
                        </div>
                        <div className="text-sm text-gray-900">
                          Client ID: {program.clientId.slice(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(program.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="w-4 h-4 mr-1 text-gray-400" />
                        {program.duration} weeks
                      </div>
                      <div className="text-xs text-gray-500">
                        {program.workoutsPerWeek}x per week
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {program.progress ? (
                        <div>
                          <div className="flex items-center">
                            <TrendingUp className="w-4 h-4 mr-1 text-green-500" />
                            <span className="text-sm font-medium">
                              {Math.round(program.progress.completionRate)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${program.progress.completionRate}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Not started</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {format(new Date(program.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleProgramAction(program.id, 'view')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="View Program"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleProgramAction(program.id, 'edit')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Edit Program"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleProgramAction(program.id, 'duplicate')}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Duplicate Program"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <div className="relative group">
                          <button className="p-1 text-gray-400 hover:text-gray-600">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                            <div className="py-1">
                              {program.status === 'draft' && (
                                <button
                                  onClick={() => handleProgramAction(program.id, 'activate')}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  Activate Program
                                </button>
                              )}
                              {program.status === 'active' && (
                                <button
                                  onClick={() => handleProgramAction(program.id, 'pause')}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                                >
                                  <Pause className="w-4 h-4 mr-2" />
                                  Pause Program
                                </button>
                              )}
                              <button
                                onClick={() => handleProgramAction(program.id, 'delete')}
                                className="w-full px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50 flex items-center"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Program
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}